import { useEffect, useState } from "react";
import { Loader2, Sparkles, DollarSign, Users, Hash } from "lucide-react";
import { client } from "../../lib/api";

/**
 * Admin "AI Usage" tab — real-time(ish) view of how much the LLM
 * surfaces are costing across the app.
 *
 * Data source: GET /api/admin/ai-usage
 *
 * Cost figures are ESTIMATES (Anthropic Sonnet 4.5 retail pricing,
 * char-based token approximation). The component surfaces a
 * disclaimer prominently so nobody reads these numbers as a final
 * invoice — EMERGENT_LLM_KEY actual billing will differ.
 */

const FEATURE_LABEL_HE = {
  chavruta: "חברותא",
  heart: "שפוך את הלב",
  lesson: "שיעור תורה",
  ai_assistant: "הסבר פסוק",
  parasha: "הברקה שבועית",
  unknown: "אחר",
};

const FEATURE_SUB_LABEL_HE = {
  chavruta: "שיחה ולמידה חופשית",
  heart: "חיזוק רגשי מהתורה",
  lesson: "בניית שיעור מותאם",
  ai_assistant: "הסברי פסוקים ופרשנים",
  parasha: "פרשת השבוע",
  unknown: "ללא תיוג פיצ'ר",
};

export default function AdminAiUsageTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get("/admin/ai-usage");
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="py-10 text-center" data-testid="admin-ai-usage-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gold inline-block" strokeWidth={1.5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-6 text-red-600 text-center text-sm" data-testid="admin-ai-usage-error">
        שגיאה בטעינת נתוני AI: {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-8" dir="rtl" data-testid="admin-ai-usage-tab">
      <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-3 text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
        <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" strokeWidth={1.6} />
        <p>
          <strong>הערה:</strong> כל סכומי הדולר הם <em>הערכה</em> לפי תעריפי
          Anthropic Sonnet 4.5 הציבוריים ($3 / מיליון input, $15 / מיליון output).
          מספר הטוקנים מוערך לפי תווים (≈3 תווים/טוקן לעברית מעורבת אנגלית).
          החיוב בפועל דרך EMERGENT_LLM_KEY עשוי להיות שונה.
        </p>
      </div>

      <WindowGroup data={data.windows} />

      <DailyChart series={data.daily_series} />
    </div>
  );
}


// =====================================================================
// 4-up window cards: today / week / month / total
// =====================================================================

function WindowGroup({ data }) {
  const windows = [
    { key: "today", title: "היום", color: "border-blue-300 dark:border-blue-700" },
    { key: "week",  title: "7 ימים אחרונים", color: "border-emerald-300 dark:border-emerald-700" },
    { key: "month", title: "30 ימים אחרונים", color: "border-violet-300 dark:border-violet-700" },
    { key: "total", title: "סה\"כ אי-פעם", color: "border-gold/40" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {windows.map((w) => (
        <WindowCard
          key={w.key}
          testKey={w.key}
          title={w.title}
          color={w.color}
          stats={data?.[w.key] || {}}
        />
      ))}
    </div>
  );
}

function WindowCard({ testKey, title, color, stats }) {
  return (
    <div
      className={`bg-card border-2 ${color} rounded-xl p-5`}
      data-testid={`admin-ai-window-${testKey}`}
    >
      <h3 className="font-heading text-base text-foreground mb-3 flex items-center justify-between">
        <span>{title}</span>
        <span
          className="text-gold font-mono text-base"
          data-testid={`admin-ai-window-${testKey}-cost`}
        >
          {fmtUsd(stats.cost_usd || 0)}
        </span>
      </h3>

      <div className="grid grid-cols-3 gap-2 text-center text-xs mb-4 border-b border-border/40 pb-3">
        <Metric icon={Hash}  label="קריאות"    value={fmtInt(stats.calls)} />
        <Metric icon={Users} label="משתמשים"   value={fmtInt(stats.unique_users)} />
        <Metric icon={DollarSign} label="טוקנים" value={fmtTokens(stats.input_tokens + stats.output_tokens)} />
      </div>

      <FeatureBreakdown features={stats.by_feature || []} totalCost={stats.cost_usd || 0} testKey={testKey} />
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div>
      <Icon className="h-3.5 w-3.5 mx-auto text-muted-foreground/70 mb-0.5" strokeWidth={1.6} />
      <div className="font-mono text-foreground">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function FeatureBreakdown({ features, totalCost, testKey }) {
  if (!features.length) {
    return (
      <p className="text-xs text-muted-foreground/70 text-center py-2">
        אין עדיין נתונים בחלון הזמן הזה.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid={`admin-ai-window-${testKey}-features`}>
      {features.map((f) => {
        const pct = totalCost > 0 ? Math.round((f.cost_usd / totalCost) * 100) : 0;
        const label = FEATURE_LABEL_HE[f.feature] || f.feature;
        const sub = FEATURE_SUB_LABEL_HE[f.feature] || "";
        return (
          <li key={f.feature} className="text-xs">
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <strong className="font-heading text-foreground">{label}</strong>
                <span className="text-muted-foreground/80 ms-2">{sub}</span>
              </div>
              <div className="text-left font-mono shrink-0">
                <span className="text-gold">{fmtUsd(f.cost_usd)}</span>
                <span className="text-muted-foreground/60 text-[10px] ms-1.5">({pct}%)</span>
              </div>
            </div>
            <div className="mt-0.5 text-[10px] text-muted-foreground/70 flex items-center gap-3">
              <span>{fmtInt(f.calls)} קריאות</span>
              <span>{fmtTokens(f.input_tokens + f.output_tokens)} טוקנים</span>
              <span>{fmtInt(f.unique_users)} משתמשים</span>
            </div>
            <div className="mt-1 h-1 bg-border/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-gold/70 rounded-full"
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}


// =====================================================================
// 30-day daily timeseries — small inline bar chart, no chart lib needed
// =====================================================================

function DailyChart({ series }) {
  if (!series || series.length === 0) {
    return (
      <div className="bg-card border border-border/60 rounded-xl p-5 text-center text-sm text-muted-foreground">
        אין נתונים יומיים זמינים — שלח שיחות AI כדי שהגרף יתעורר.
      </div>
    );
  }
  const maxCost = Math.max(0.0001, ...series.map((d) => d.cost_usd));
  const totalCost = series.reduce((a, b) => a + b.cost_usd, 0);
  const totalCalls = series.reduce((a, b) => a + b.calls, 0);

  return (
    <div className="bg-card border border-border/60 rounded-xl p-5" data-testid="admin-ai-daily-chart">
      <header className="flex items-baseline justify-between mb-3">
        <h3 className="font-heading text-base text-foreground">שימוש יומי · 30 ימים אחרונים</h3>
        <div className="text-xs text-muted-foreground font-mono">
          <span className="text-gold">{fmtUsd(totalCost)}</span>
          <span className="mx-2">·</span>
          <span>{fmtInt(totalCalls)} קריאות</span>
        </div>
      </header>

      <div className="flex items-end gap-1 h-32 border-b border-border/60 pb-1">
        {series.map((d) => {
          const height = Math.max(2, (d.cost_usd / maxCost) * 100);
          return (
            <div
              key={d.date}
              className="flex-1 group relative"
              data-testid={`admin-ai-bar-${d.date}`}
            >
              <div
                className="bg-gold/70 hover:bg-gold transition-colors rounded-sm"
                style={{ height: `${height}%` }}
                title={`${d.date}\n${fmtUsd(d.cost_usd)}\n${fmtInt(d.calls)} calls`}
              />
              <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-12 right-1/2 translate-x-1/2 bg-foreground text-background text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap pointer-events-none z-10">
                {d.date} · {fmtUsd(d.cost_usd)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex justify-between text-[10px] text-muted-foreground font-mono">
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}


// =====================================================================
// Formatters — Hebrew-locale aware, $ with cents-precision on small amounts
// =====================================================================

function fmtUsd(n) {
  const v = Number(n) || 0;
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  if (v > 0) return `$${v.toFixed(5)}`;
  return "$0.00";
}

function fmtInt(n) {
  return new Intl.NumberFormat("he-IL").format(Math.round(Number(n) || 0));
}

function fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return String(Math.round(v));
}
